import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nav, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import NotificationItem from './NotificationItem';
import { Auth } from 'aws-amplify';
import io from 'socket.io-client';
const { ApiHelper } = require('../../utils/ApiHelper');
const { GET_NOTIFICATIONS, GET_UNREAD_COUNT, MARK_NOTIFICATION_READ } = require('../../lib/API_KEYS');

const ActivityNotifications = ({ userRole, userId }) => {
  const [activeTab, setActiveTab] = useState('activities');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({
    activities: 0,
    platform: 0,
    admin: 0
  });
  const [socket, setSocket] = useState(null);
  const navigate = useNavigate();

  // Fetch notifications for active tab
  const fetchNotifications = async (type) => {
    try {
      setLoading(true);
      const typeMap = {
        activities: 'activity',
        platform: 'platform',
        admin: 'admin'
      };
      
      const res = await new ApiHelper().FetchFromServer(
        ...GET_NOTIFICATIONS,
        { type: typeMap[type], limit: 10 }
      );
      
      // ApiHelper wraps response: res.data.data contains the actual array
      const notifs = Array.isArray(res?.data?.data) ? res.data.data : [];
      setNotifications(notifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]); // Safe fallback
      // Don't show error toast - silent failure for better UX
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread counts
  const fetchUnreadCounts = async () => {
    try {
      const res = await new ApiHelper().FetchFromServer(
        ...GET_UNREAD_COUNT
      );
      
      if (res.data) {
        setUnreadCounts({
          activities: res.data.activity || 0,
          platform: res.data.platform || 0,
          admin: res.data.admin || 0
        });
      }
    } catch (error) {
      console.error('Error fetching unread counts:', error);
    }
  };

  // Initialize socket connection for real-time notifications
  useEffect(() => {
    const initSocket = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const mongoUserId = userId || user.attributes['custom:mongoId'];
        
        const socketUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
        const newSocket = io(socketUrl, {
          transports: ['websocket', 'polling'],
        });
        
        newSocket.on('connect', () => {
          console.log('Socket connected for notifications');
          // Join user-specific room
          newSocket.emit('joinUserRoom', mongoUserId);
        });
        
        // Listen for new notifications
        newSocket.on('notification', (notification) => {
          console.log('New notification received:', notification);
          
          // Update unread counts
          setUnreadCounts(prev => ({
            ...prev,
            activities: notification.type === 'activity' ? prev.activities + 1 : prev.activities,
            platform: notification.type === 'platform' ? prev.platform + 1 : prev.platform,
            admin: notification.type === 'admin' ? prev.admin + 1 : prev.admin
          }));
          
          // If viewing the same tab, add to list
          const typeMap = {
            activity: 'activities',
            platform: 'platform',
            admin: 'admin'
          };
          
          if (typeMap[notification.type] === activeTab) {
            setNotifications(prev => [notification, ...prev].slice(0, 10));
          }
          
          // Show toast notification
          toast.info(notification.title, {
            position: 'top-right',
            autoClose: 5000,
          });
        });
        
        setSocket(newSocket);
        
        return () => {
          newSocket.disconnect();
        };
      } catch (error) {
        console.error('Error initializing socket:', error);
      }
    };
    
    initSocket();
  }, [userId]);

  // Fetch notifications when tab changes
  useEffect(() => {
    fetchNotifications(activeTab);
  }, [activeTab]);

  // Fetch unread counts on mount
  useEffect(() => {
    fetchUnreadCounts();
  }, []);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      if (!notification.read) {
        await new ApiHelper().FetchFromServer(
          MARK_NOTIFICATION_READ[0].replace(':id', notification._id),
          MARK_NOTIFICATION_READ[1]
        );
        
        // Update local state
        setNotifications(prev =>
          prev.map(n => n._id === notification._id ? { ...n, read: true } : n)
        );
        
        // Update unread count
        setUnreadCounts(prev => ({
          ...prev,
          [activeTab]: Math.max(0, prev[activeTab] - 1)
        }));
      }
      
      // Navigate based on entity type
      if (notification.entityType === 'company') {
        // Registration notifications - navigate to application form or admin review
        if (userRole === 'admin') {
          // Extract company type from title (e.g., "Buyer Application" or "New Seller Application")
          const title = notification.title || '';
          let companyRoute = 'buyers'; // default
          
          if (title.toLowerCase().includes('seller')) {
            companyRoute = 'sellers';
          } else if (title.toLowerCase().includes('pm') || title.toLowerCase().includes('manager') || title.toLowerCase().includes('project manager')) {
            companyRoute = 'project-managers';
          }
          
          // Admin viewing company application
          navigate(`/admin-dashboard/${companyRoute}/${notification.entityId}`);
        } else {
          // User viewing their own application status
          if (notification.title.includes('Approved')) {
            // Approved users go to settings (view-only)
            navigate(`/${userRole}-dashboard/settings`);
          } else {
            // Rejected/Concerns users go to form (can edit)
            navigate(`/${userRole}-dashboard/additional-info`);
          }
        }
      } else if (notification.entityType === 'rfq' || notification.entityType === 'sellerRfq') {
        // RFQ notifications - navigate to requirements/RFQs page (no communications workspace yet)
        if (userRole === 'admin') {
          navigate('/admin-dashboard/rfqs');
        } else if (userRole === 'buyer') {
          navigate(`/buyer-dashboard/review-sellers/${notification.entityId}`);
        } else {
          // Sellers
          navigate(`/seller-dashboard/requirements`);
        }
      } else if (notification.entityType === 'project') {
        // Project notifications - navigate to communications workspace
        navigate(`/${userRole}-dashboard/communication/${notification.entityId}`);
      }
      // Other entity types (milestone, feedback) - just mark as read
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="notification-section border rounded-4 shadow-sm p-4 mt-4">
      <h5 className="mb-3 fw-semibold">Activities</h5>
      
      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'activities'}
            onClick={() => handleTabChange('activities')}
            style={{ cursor: 'pointer' }}
          >
            Activities {unreadCounts.activities > 0 && (
              <Badge bg="primary" className="ms-1">{unreadCounts.activities}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'platform'}
            onClick={() => handleTabChange('platform')}
            style={{ cursor: 'pointer' }}
          >
            Platform Updates {unreadCounts.platform > 0 && (
              <Badge bg="primary" className="ms-1">{unreadCounts.platform}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'admin'}
            onClick={() => handleTabChange('admin')}
            style={{ cursor: 'pointer' }}
          >
            Admin Notifications {unreadCounts.admin > 0 && (
              <Badge bg="primary" className="ms-1">{unreadCounts.admin}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="notification-list">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center text-muted py-5">
            <img 
              src="/images/fileUpload.svg" 
              alt="No notifications" 
              width={48}
              height={48}
              style={{ opacity: 0.3, marginBottom: '12px' }}
            />
            <p className="mb-0">No notifications</p>
          </div>
        ) : (
          <>
            {notifications.map((notif) => (
              <NotificationItem 
                key={notif._id}
                notification={notif}
                onClick={() => handleNotificationClick(notif)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityNotifications;